import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  PLATFORM_ID,
  signal,
  ViewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { RouterLink } from '@angular/router';
import { ChevronLeft, ChevronRight, LucideIconData } from 'lucide-angular';
import { IconComponent } from '../../../../shared/ui/icon/icon.component';
import { CategoryItem } from '../../data/mock-categories';

const AUTOPLAY_MS = 4000;
const AUTOPLAY_RESUME_DELAY = 8000;

@Component({
  selector: 'app-categories-carousel-block',
  imports: [RouterLink, IconComponent],
  templateUrl: './categories-carousel-block.component.html',
  styleUrl: './categories-carousel-block.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CategoriesCarouselBlockComponent {
  @ViewChild('track') private trackRef!: ElementRef<HTMLElement>;

  readonly categories = input.required<CategoryItem[]>();
  readonly loading = input(false);

  protected readonly chevronLeft: LucideIconData = ChevronLeft;
  protected readonly chevronRight: LucideIconData = ChevronRight;
  protected readonly skeletonItems: readonly number[] = [0, 1, 2, 3, 4];

  protected readonly isPaused = signal(false);
  protected readonly isReducedMotion = signal(false);

  private readonly visibleCount = signal(1);
  private readonly currentIndex = signal(0);
  private readonly maxIndex = computed(() =>
    Math.max(0, this.categories().length - this.visibleCount()),
  );

  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly destroyRef = inject(DestroyRef);
  private resumeTimerId: number | null = null;

  constructor() {
    if (this.isBrowser) {
      this.bindReducedMotionPreference();
    }

    effect((onCleanup) => {
      const loading = this.loading();
      const paused = this.isPaused();
      const reducedMotion = this.isReducedMotion();

      if (!this.isBrowser || loading || paused || reducedMotion) return;

      const timerId = window.setInterval(() => {
        this.advanceOne();
      }, AUTOPLAY_MS);

      onCleanup(() => window.clearInterval(timerId));
    });

    afterNextRender(() => {
      const el = this.trackRef?.nativeElement;
      if (!el) return;

      this.updateVisibleCount(el);

      const onScroll = (): void => this.onTrackScroll(el);
      el.addEventListener('scroll', onScroll, { passive: true });

      let resizeObserver: ResizeObserver | null = null;
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => this.updateVisibleCount(el));
        resizeObserver.observe(el);
      }

      this.destroyRef.onDestroy(() => {
        el.removeEventListener('scroll', onScroll);
        resizeObserver?.disconnect();
        if (this.resumeTimerId) window.clearTimeout(this.resumeTimerId);
      });
    });
  }

  protected scrollPrev(): void {
    if (!this.isBrowser) return;
    this.trackRef?.nativeElement.scrollBy({ left: -this.getCardStep(), behavior: 'smooth' });
    this.pauseAndScheduleResume();
  }

  protected scrollNext(): void {
    if (!this.isBrowser) return;
    this.trackRef?.nativeElement.scrollBy({ left: this.getCardStep(), behavior: 'smooth' });
    this.pauseAndScheduleResume();
  }

  protected onWrapperMouseEnter(): void {
    this.isPaused.set(true);
  }

  protected onWrapperMouseLeave(): void {
    if (!this.isReducedMotion()) this.isPaused.set(false);
  }

  protected onWrapperFocusIn(): void {
    this.isPaused.set(true);
  }

  protected onWrapperFocusOut(event: FocusEvent): void {
    const root = this.trackRef?.nativeElement?.parentElement;
    const next = event.relatedTarget;
    if (root && next instanceof Node && root.contains(next)) return;
    if (!this.isReducedMotion()) this.isPaused.set(false);
  }

  protected onWrapperTouchStart(): void {
    this.isPaused.set(true);
  }

  protected onWrapperTouchEnd(): void {
    this.scheduleAutoplayResume();
  }

  private advanceOne(): void {
    const el = this.trackRef?.nativeElement;
    if (!el) return;

    const next = this.currentIndex() + 1;
    if (next > this.maxIndex()) {
      el.scrollTo({ left: 0, behavior: 'smooth' });
      this.currentIndex.set(0);
    } else {
      el.scrollBy({ left: this.getCardStep(), behavior: 'smooth' });
      this.currentIndex.set(next);
    }
  }

  private onTrackScroll(el: HTMLElement): void {
    const step = this.getCardStep();
    const rawIndex = Math.round(el.scrollLeft / step);
    this.currentIndex.set(Math.max(0, Math.min(rawIndex, this.maxIndex())));
  }

  private updateVisibleCount(el: HTMLElement): void {
    const step = this.getCardStep();
    this.visibleCount.set(Math.max(1, Math.floor(el.clientWidth / step)));
  }

  private getCardStep(): number {
    const firstCard = this.trackRef?.nativeElement?.firstElementChild as HTMLElement | null;
    return firstCard?.offsetWidth ?? 200;
  }

  private pauseAndScheduleResume(): void {
    this.isPaused.set(true);
    this.scheduleAutoplayResume();
  }

  private scheduleAutoplayResume(): void {
    if (this.resumeTimerId) window.clearTimeout(this.resumeTimerId);
    this.resumeTimerId = window.setTimeout(() => {
      if (!this.isReducedMotion()) this.isPaused.set(false);
      this.resumeTimerId = null;
    }, AUTOPLAY_RESUME_DELAY);
  }

  private bindReducedMotionPreference(): void {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = (matches: boolean): void => {
      this.isReducedMotion.set(matches);
      if (matches) this.isPaused.set(true);
    };
    sync(query.matches);

    const onChange = (e: MediaQueryListEvent): void => sync(e.matches);
    query.addEventListener('change', onChange);
    this.destroyRef.onDestroy(() => query.removeEventListener('change', onChange));
  }
}
